import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchNoticias,
  fetchNoticiaById,
  clearNoticiaSeleccionada,
} from "../redux/noticiasSlice";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Placeholder,
  Pagination,
} from "react-bootstrap";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import "../styles/Noticias.css";
import RichTextNoticias from "../components/Noticias/RichTextNoticias";
import YouTubeEmbed from "../components/YouTubeEmbed";
import "../styles/youtube.css";


// =========================
// Helpers fuera del componente
// =========================
const parseFechaLocal = (fechaString) => {
  if (!fechaString) return null;

  const str = String(fechaString).trim();
  if (!str) return null;

  // Soporta "2025-11-11", "2025-11-11 15:44:11", "2025-11-11T15:44:11Z"
  const [datePart] = str.split(/[T ]/);
  const [year, month, day] = datePart.split("-").map(Number);

  if (!year || !month || !day) return null;

  // Fecha local (no UTC) => evita que se reste un día por zona horaria
  return new Date(year, month - 1, day);
};

const getTimestamp = (fecha_publicacion, created_at) => {
  const fecha = parseFechaLocal(fecha_publicacion || created_at);
  if (!fecha || Number.isNaN(fecha.getTime())) return 0;
  return fecha.getTime();
};

const formatFecha = (fechaString) => {
  const fecha = parseFechaLocal(fechaString);
  if (!fecha || Number.isNaN(fecha.getTime())) {
    return "Fecha no disponible";
  }
  const opciones = { day: "numeric", month: "long", year: "numeric" };
  return fecha.toLocaleDateString("es-CL", opciones);
};

const IMAGE_URL_REGEX =
  /(https?:\/\/[^\s]+?\.(?:png|jpe?g|gif|webp|bmp|svg))/i;

// Máx 4 filas de 3 cards en escritorio -> 12 por página
const ITEMS_PER_PAGE = 12;

const buildDescripcionPreview = (descripcion, maxLength = 100) => {
  const raw = String(descripcion ?? "").trim();
  if (!raw) {
    return { preview: "", hasImage: false };
  }

  // ¿Hay alguna URL de imagen?
  const hasImage = IMAGE_URL_REGEX.test(raw);

  // Quitamos las URLs de imagen del texto para que no se vean en la card
  const sinImagenes = raw.replace(IMAGE_URL_REGEX, "").trim();

  const preview =
    sinImagenes.length > maxLength
      ? sinImagenes.substring(0, maxLength)
      : sinImagenes;

  return { preview, hasImage };
};


// =========================
// Componente principal
// =========================

const NoticiasPage = () => {
  const dispatch = useDispatch();
  const { noticias, noticiaSeleccionada, loading, error } = useSelector(
    (state) => state.noticias
  );

  const [mostrarDetalle, setMostrarDetalle] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);


  useEffect(() => {
    dispatch(fetchNoticias());
  }, [dispatch]);

  // Ordenar sin mutar el estado: más reciente -> más antigua
  const noticiasOrdenadas = useMemo(() => {
    const safe = Array.isArray(noticias) ? noticias : [];
    return [...safe].sort((a, b) => {
      const da = getTimestamp(a?.fecha_publicacion, a?.created_at);
      const db = getTimestamp(b?.fecha_publicacion, b?.created_at);

      // Manejo de fechas inválidas
      if (!db && !da) return 0;
      if (!db) return 1;
      if (!da) return -1;

      return db - da; // DESC -> más reciente primero
    });
  }, [noticias]);

  // resetear a página 1 cuando cambian las noticias
  useEffect(() => {
    setCurrentPage(1);
  }, [noticias]);

  // total de páginas y slice de noticias por página
  const totalPages = useMemo(() => {
    if (!Array.isArray(noticiasOrdenadas) || noticiasOrdenadas.length === 0) {
      return 1;
    }
    return Math.ceil(noticiasOrdenadas.length / ITEMS_PER_PAGE);
  }, [noticiasOrdenadas]);

  const noticiasPagina = useMemo(() => {
    if (!Array.isArray(noticiasOrdenadas)) return [];
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return noticiasOrdenadas.slice(
      startIndex,
      startIndex + ITEMS_PER_PAGE
    );
  }, [noticiasOrdenadas, currentPage]);

  const handleVerDetalles = (id) => {
    dispatch(fetchNoticiaById(id));
    setMostrarDetalle(true);
  };

  const handleVolver = () => {
    setMostrarDetalle(false);
    dispatch(clearNoticiaSeleccionada());
  };

  const obtenerImagenPredeterminada = () => {
    const ancho = 400;
    const alto = 200;
    return `https://picsum.photos/${ancho}/${alto}?grayscale`;
  };

  return (
    <Container
      className="my-5"
      style={{ maxWidth: "1200px" }}
      role="main"
      aria-label="Página de noticias"
    >
      {/* Header con logo y título */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "2rem",
          gap: "1rem",
          justifyContent: "flex-start",
        }}
        aria-label="Encabezado de noticias"
      >
        <img
          src="https://api.sinterec.cl/public/images/SINTEREC_logo.jpg"
          alt="Logo SINTEREC"
          className="sindicato-logo"
          aria-hidden="true"
        />
        <h1 className="noticias-title" aria-level="1">
          Noticias
        </h1>
      </div>

      {error && (
        <p
          className="text-danger text-center"
          role="alert"
          aria-live="assertive"
        >
          Error: {error}
        </p>
      )}

      {!loading && (Array.isArray(noticias) ? noticias.length === 0 : true) && (
        <p className="text-center" aria-live="polite">
          No hay noticias disponibles en este momento.
        </p>
      )}

      {loading ? (
        <Row aria-label="Noticias cargando">
          {Array.from({ length: 6 }).map((_, index) => (
            <Col key={index} xs={12} md={6} lg={4} className="mb-4">
              <Card className="noticias-card" aria-hidden="true">
                <div
                  className="card-img-container loading-placeholder"
                  aria-hidden="true"
                />
                <Card.Body>
                  <Placeholder as={Card.Title} animation="glow">
                    <Placeholder xs={8} className="loading-placeholder" />
                  </Placeholder>
                  <Placeholder as={Card.Text} animation="glow">
                    <Placeholder xs={10} className="loading-placeholder" />
                    <Placeholder xs={6} className="loading-placeholder" />
                    <Placeholder xs={8} className="loading-placeholder" />
                  </Placeholder>
                  <Placeholder
                    as={Button}
                    animation="glow"
                    className="btn-ver-mas loading-placeholder"
                    xs={6}
                  />
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      ) : ( // <-- Inicio del "else" de la carga (loading = false)
        !mostrarDetalle &&
        noticiasOrdenadas.length > 0 && (
          <>
            <Row role="list" aria-label="Lista de noticias">
              {noticiasPagina.map((noticia) => {
                const { preview, hasImage } = buildDescripcionPreview(
                  noticia.descripcion,
                  100
                );
                const textoPreview =
                  preview || (hasImage ? "Noticia con imagen" : "");

                return (
                  <Col
                    key={noticia.id}
                    xs={12}
                    md={6}
                    lg={4}
                    className="mb-4"
                    role="listitem"
                  >
                    <Card className="noticias-card">
                      <div className="card-img-container">
                        <LazyLoadImage
                          src={
                            noticia.imagen_url || obtenerImagenPredeterminada()
                          }
                          alt={`Imagen de la noticia: ${noticia.titulo}`}
                          effect="blur"
                          className="w-100 h-100 object-fit-cover"
                          aria-hidden="false"
                        />
                      </div>
                      <Card.Body>
                        <Card.Title aria-level="2">
                          {noticia.titulo}
                        </Card.Title>

                        <Card.Text
                          aria-label={`Descripción breve: ${textoPreview}${
                            hasImage
                              ? ". Esta noticia incluye imágenes."
                              : ""
                          }`}
                        >
                          {textoPreview && `${textoPreview}...`}
                          {hasImage && (
                            <span
                              className="ms-2"
                              aria-hidden="true"
                              title="Esta noticia incluye imágenes"
                            >
                              <i className="fa-regular fa-image" />
                            </span>
                          )}
                        </Card.Text>

                        {/* Fecha visible también en la tarjeta */}
                        <div
                          className="small text-muted mb-2"
                          aria-label={`Fecha de publicación: ${formatFecha(
                            noticia.fecha_publicacion || noticia.created_at
                          )}`}
                        >
                          {formatFecha(
                            noticia.fecha_publicacion || noticia.created_at
                          )}
                        </div>

                        <Button
                          variant="primary"
                          className="btn-ver-mas"
                          onClick={() => handleVerDetalles(noticia.id)}
                          aria-label={`Ver detalles completos sobre: ${noticia.titulo}`}
                        >
                          Ver más
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>

            {/* 👇 Paginación debajo de las cards */}
            {totalPages > 1 && (
              <div
                className="d-flex justify-content-center mt-3"
                aria-label="Paginación de noticias"
              >
                <Pagination className="mb-0">
                  <Pagination.Prev
                    disabled={currentPage === 1}
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                  />
                  {Array.from({ length: totalPages }).map((_, index) => {
                    const page = index + 1;
                    return (
                      <Pagination.Item
                        key={page}
                        active={page === currentPage}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </Pagination.Item>
                    );
                  })}
                  <Pagination.Next
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setCurrentPage((prev) =>
                        Math.min(totalPages, prev + 1)
                      )
                    }
                  />
                </Pagination>
              </div>
            )}
          </>
        )
      )} {/* <-- AQUI TERMINA EL TERNARIO DE 'loading' CORRECTAMENTE */}


      {mostrarDetalle && noticiaSeleccionada && (
        <div
          className="noticia-detalle-container"
          role="article"
          aria-label="Detalle de noticia"
        >
          <Button
            variant="primary"
            className="btn-volver mb-4"
            onClick={handleVolver}
            aria-label="Volver al listado de noticias"
          >
            Volver a Noticias
          </Button>

          <Card className="detalle-card">
            <div className="detalle-img-container">
              <LazyLoadImage
                src={
                  noticiaSeleccionada.imagen_url ||
                  obtenerImagenPredeterminada()
                }
                alt={`Imagen destacada de la noticia: ${noticiaSeleccionada.titulo}`}
                effect="blur"
                className="w-100 h-100 object-fit-cover"
              />
            </div>

            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Card.Title className="noticia-titulo m-0" aria-level="1">
                  {noticiaSeleccionada.titulo}
                </Card.Title>
                <span
                  className="noticia-fecha badge bg-secondary"
                  aria-label={`Fecha de publicación: ${formatFecha(
                    noticiaSeleccionada.fecha_publicacion ||
                      noticiaSeleccionada.created_at
                  )}`}
                >
                  {formatFecha(
                    noticiaSeleccionada.fecha_publicacion ||
                      noticiaSeleccionada.created_at
                  )}
                </span>
              </div>

              <div
                className="noticia-contenido card p-3"
                aria-label="Contenido completo de la noticia"
              >
                <div className="noticia-descripcion">
                  <RichTextNoticias
                    text={noticiaSeleccionada.descripcion || ""}
                  />
                </div>
              </div>


              {/* Video de YouTube, solo si la noticia tiene uno válido */}
              {noticiaSeleccionada.video_url && (
                <YouTubeEmbed
                  url={noticiaSeleccionada.video_url}
                  titulo={noticiaSeleccionada.titulo}
                />
              )}
            </Card.Body>
          </Card>
        </div>
      )}
    </Container>
  );
};

export default NoticiasPage;